import {observer} from 'mobx-react-lite'
import React, {useMemo} from 'react'
import tw from 'tailwind-styled-components'
import {ArrUtil} from 'yougile-util'
import {DataObjectId, Hub, Tag} from 'yougile-model'
import {Backend} from '@common/backend'
import {useStickerStore} from '@components/sticker/sticker-store'
import {Sticker} from '@extensions/sticker/sticker'
import {StickerItemData} from '@extensions/sticker/views-web/a-sticker-view'
import {UserIcon} from '@extensions/sticker/views-web/user/user-sticker-state'

const TwUserSticker = tw.div<{
  $invisible: boolean
}>`
  group-hover:visible
  relative
  flex
  justify-end
  cursor-pointer
  ${({$invisible}) => ($invisible ? 'invisible' : '')}
  relative
`

const TwStickerRequired = tw.div`
  absolute
  right-0
  -top-[6px]
  text-notification-primary
  text-12
`

interface UserStickerProps {
  onClick?: () => void
  isDraggable?: boolean
  className?: string
  required?: boolean
}
export const UserSticker = observer(
  ({onClick, isDraggable, className, required}: UserStickerProps) => {
    const [state, store] = useStickerStore()

    const users = useAssignedUsers(
      store.sticker,
      state.value,
      store.stickerTask
    )
    const shift = users.length > 2 ? 14 : 12
    const getMaxWidth = () => {
      const usersLength = (users.length > 3 ? 3 : users.length) - 1
      return 24 + (usersLength === 1 ? 12 : 21)
    }

    return (
      <TwUserSticker
        data-test="board-user-sticker"
        $invisible={!state.menuIsOpened && !users.length}
        className={className}
        onClick={onClick}
        draggable={Boolean(isDraggable)}
      >
        {!users.length && (
          <div className="p-2">
            <UserIcon
              hasTransparentStyle={state.hasTransparentStyle}
              active={state.menuIsOpened}
              isCompleted={state.taskIsCompleted}
              size={16}
              disableShift
              hideTitle
            />
          </div>
        )}
        {users.length > 0 && (
          <div
            className={`${
              state.hasTransparentStyle && state.taskIsCompleted
                ? 'opacity-60 hover:opacity-100'
                : 'opacity-100'
            } flex flex-row-reverse`}
            style={{
              maxWidth: `${getMaxWidth()}px`
            }}
          >
            {users.slice(0, 3).map((userId, ix) => (
              <div
                key={userId}
                className="relative flex rounded-full p-2 bg-background-transparent"
                style={{
                  left: shift * ix,
                  zIndex: 100 + ix
                }}
              >
                <UserIcon
                  userId={userId}
                  size={20}
                  hideTitle
                  cursor="pointer"
                />
              </div>
            ))}
          </div>
        )}
        {required && <TwStickerRequired>*</TwStickerRequired>}
      </TwUserSticker>
    )
  }
)

export const useAssignedUsers = (
  sticker: Tag,
  usersList: StickerItemData['currentValue'],
  stickerTask?: Hub
) => {
  return useMemo(() => {
    return ArrUtil.filterNil(
      stickerTask
        ? Sticker.getUsers(stickerTask, sticker)
        : getDataUsers(usersList)
    )
  }, [usersList, stickerTask])
}

export const getDataUsers = (
  currentValue: StickerItemData['currentValue']
): DataObjectId[] => {
  if (!currentValue) {
    return []
  }

  return Array.isArray(currentValue)
    ? currentValue.map(id => (id === 'author' ? Backend.loadId() : id))
    : [currentValue]
}
